import snsWebSdk from '@sumsub/websdk';
import { isLeft, isRight } from 'fp-ts/lib/Either';
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { NavigateFunction } from 'react-router';
import type { TRPC } from '../../contexts/TRPC';
import { SumsubEventTracker } from '../../lib/sumsubEventTracker';
import { unknownToLeft } from '../../lib/unknownToLeft';
import type { KybStatusData } from '../../lib/verificationStatus';
import { captureAndReportError } from '../../utils/errorReporting';
import type { SumsubVerificationConfig } from './types';

interface SumsubReviewResult {
  reviewAnswer?: string;
  reviewRejectType?: string;
  moderationComment?: string;
  clientComment?: string;
}

interface SumsubReviewPayload {
  reviewStatus?: string;
  reviewResult?: SumsubReviewResult;
}

export interface UseSumsubSdkLifecycleArgs {
  trpc: TRPC['client'];
  config: SumsubVerificationConfig;
  kybSubmissionId: string | undefined;
  canResume: boolean;
  setKybStatus: Dispatch<SetStateAction<KybStatusData | null>>;
  refreshKybStatus: () => Promise<void>;
  refreshPublisher: () => Promise<void>;
  navigate: NavigateFunction;
  sdkContainerRef: RefObject<HTMLDivElement | null>;
  activePublisherJoinId: string | undefined;
  isPublisherLoading: boolean;
}

export interface UseSumsubSdkLifecycleResult {
  error: string;
  notice: string;
  loading: boolean;
  showSdk: boolean;
  initializeSumsubSdk: (options?: { forceFresh?: boolean }) => Promise<void>;
  restartVerification: () => Promise<void>;
}

export function useSumsubSdkLifecycle({
  trpc,
  config,
  kybSubmissionId,
  canResume,
  setKybStatus,
  refreshKybStatus,
  refreshPublisher,
  navigate,
  sdkContainerRef,
  activePublisherJoinId,
  isPublisherLoading,
}: UseSumsubSdkLifecycleArgs): UseSumsubSdkLifecycleResult {
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSdk, setShowSdk] = useState(false);
  const eventTrackerRef = useRef<SumsubEventTracker | null>(null);
  const sdkInteractionStartedRef = useRef(false);
  const isResumingRef = useRef(false);

  const updateSdkInteractionStarted = (started: boolean) => {
    sdkInteractionStartedRef.current = started;
  };

  const resetVerificationView = () => {
    setError('');
    setNotice('');
    setLoading(false);
    setShowSdk(false);
    updateSdkInteractionStarted(false);
    isResumingRef.current = false;

    if (sdkContainerRef.current) {
      sdkContainerRef.current.innerHTML = '';
    }

    if (eventTrackerRef.current) {
      eventTrackerRef.current.flush();
      eventTrackerRef.current = null;
    }
  };

  useEffect(() => {
    resetVerificationView();
  }, [activePublisherJoinId, isPublisherLoading, trpc]);

  const markKybSubmissionAsSubmitted = async () => {
    const result = await trpc.markKybAsSubmitted
      .mutate({
        kybSubmissionId,
      })
      .catch(unknownToLeft);

    if (isLeft(result)) {
      captureAndReportError(result.left);
      setError('Failed to update verification status');
      return false;
    }

    setKybStatus((currentStatus) => {
      if (!currentStatus) {
        return currentStatus;
      }

      return {
        ...currentStatus,
        status:
          currentStatus.status === 'NotStarted' ||
          currentStatus.status === 'RequiresAdditionalInfo'
            ? 'InProgress'
            : currentStatus.status,
        submittedAt: currentStatus.submittedAt || new Date().toISOString(),
      };
    });

    return true;
  };

  const markKybSubmissionAsSubmittedIfNeeded = async () => {
    if (!sdkInteractionStartedRef.current && isResumingRef.current) {
      return true;
    }

    return markKybSubmissionAsSubmitted();
  };

  const handleFinalReviewResult = async (
    payload: SumsubReviewPayload,
    source: string,
  ) => {
    const reviewAnswer = payload.reviewResult?.reviewAnswer;

    console.log('Applicant review result received:', {
      source,
      reviewStatus: payload.reviewStatus,
      reviewAnswer,
      reviewRejectType: payload.reviewResult?.reviewRejectType,
    });

    if (payload.reviewStatus !== 'completed') {
      return;
    }

    switch (reviewAnswer) {
      case 'GREEN': {
        setError('');
        setNotice('');

        const result = await markKybSubmissionAsSubmitted();
        if (!result) {
          setError('Verification completed but failed to update status');
          return;
        }
        await refreshPublisher();
        navigate('/dashboard');
        break;
      }
      case 'RED':
        setNotice('');
        setShowSdk(false);
        setError(
          'Verification was not approved. Please contact support if you need assistance.',
        );
        await refreshKybStatus();
        break;
      case 'YELLOW':
        setError('');
        setShowSdk(false);
        setNotice(
          'Verification was submitted successfully. Sumsub is still determining the final result.',
        );
        await refreshKybStatus();
        break;
      default:
        console.log('Review completed without a final reviewAnswer', {
          source,
          reviewStatus: payload.reviewStatus,
          reviewResult: payload.reviewResult,
        });
    }
  };

  const resetVerification = async () => {
    const result = await trpc.resetSumsubVerification
      .mutate({
        kybSubmissionId,
      })
      .catch(unknownToLeft);

    if (isLeft(result)) {
      captureAndReportError('Verification reset failed:', result.left);
      setError('Failed to start a new verification');
      return false;
    }

    await refreshKybStatus();
    return true;
  };

  const initializeSumsubSdk = async (options?: { forceFresh?: boolean }) => {
    setLoading(true);
    setError('');
    setNotice('');
    updateSdkInteractionStarted(false);

    try {
      const shouldResume = !options?.forceFresh && canResume;
      isResumingRef.current = shouldResume;

      const tokenResult = await trpc.generateSumsubAccessToken
        .mutate({
          levelName: config.levelName,
          kybSubmissionId,
          resumeExisting: shouldResume,
        })
        .catch(unknownToLeft);

      if (isLeft(tokenResult)) {
        captureAndReportError('Token generation failed:', tokenResult.left);
        const error = tokenResult.left;
        const errorMessage =
          error instanceof Error
            ? error.message
            : typeof error === 'object' && error && 'message' in error
              ? String((error as { message: unknown }).message)
              : 'Unknown error';
        setError(`Failed to initialize verification: ${errorMessage}`);
        setLoading(false);
        return;
      }

      if (!isRight(tokenResult)) {
        setError('Failed to initialize verification');
        setLoading(false);
        return;
      }

      const { token } = tokenResult.right;

      if (!token || typeof token !== 'string') {
        captureAndReportError('Invalid token received:', token);
        setError('Invalid access token received from server');
        setLoading(false);
        return;
      }

      if (sdkContainerRef.current) {
        sdkContainerRef.current.innerHTML = '';
      }

      setShowSdk(true);
      setLoading(false);

      setTimeout(() => {
        if (sdkContainerRef.current) {
          eventTrackerRef.current = new SumsubEventTracker(
            trpc,
            config.levelName,
            kybSubmissionId,
          );
          const eventHandlers = eventTrackerRef.current.createEventHandlers();

          const snsWebSdkInstance = snsWebSdk
            .init(token, async () => {
              const newTokenResult = await trpc.generateSumsubAccessToken
                .mutate({
                  levelName: config.levelName,
                  kybSubmissionId,
                  resumeExisting: shouldResume,
                })
                .catch(unknownToLeft);
              if (isLeft(newTokenResult)) {
                throw new Error('Failed to refresh token');
              }
              if (!isRight(newTokenResult)) {
                throw new Error('Failed to refresh token');
              }
              return newTokenResult.right.token;
            })
            .withConf({
              lang: 'en',
              theme: 'dark',
            })
            .withOptions({
              addViewportTag: false,
              adaptIframeHeight: true,
            })
            .on('idCheck.onReady', eventHandlers['idCheck.onReady'])
            .on('idCheck.onInitialized', eventHandlers['idCheck.onInitialized'])
            .on(
              'idCheck.onLivenessCompleted',
              eventHandlers['idCheck.onLivenessCompleted'],
            )
            .on('idCheck.onApplicantResubmitted', (payload) => {
              console.log('Applicant resubmitted documents:', payload);
              updateSdkInteractionStarted(true);
              eventHandlers['idCheck.onApplicantResubmitted'](payload);
            })
            .on(
              'idCheck.onApplicantActionLoaded',
              eventHandlers['idCheck.onApplicantActionLoaded'],
            )
            .on(
              'idCheck.onApplicantActionSubmitted',
              eventHandlers['idCheck.onApplicantActionSubmitted'],
            )
            .on(
              'idCheck.onApplicantActionStatusChanged',
              eventHandlers['idCheck.onApplicantActionStatusChanged'],
            )
            .on('idCheck.onUploadError', eventHandlers['idCheck.onUploadError'])
            .on(
              'idCheck.onUploadWarning',
              eventHandlers['idCheck.onUploadWarning'],
            )
            .on(
              'idCheck.onVideoIdentCallStarted',
              eventHandlers['idCheck.onVideoIdentCallStarted'],
            )
            .on(
              'idCheck.onVideoIdentModeratorJoined',
              eventHandlers['idCheck.onVideoIdentModeratorJoined'],
            )
            .on(
              'idCheck.onVideoIdentCompleted',
              eventHandlers['idCheck.onVideoIdentCompleted'],
            )
            .on(
              'idCheck.onNavigationUiControlsStateChanged',
              eventHandlers['idCheck.onNavigationUiControlsStateChanged'],
            )
            .on('idCheck.onStepInitiated', (payload) => {
              console.log('Verification step initiated:', payload);
              eventHandlers['idCheck.onStepInitiated'](payload);
              if (payload.idDocSetType) {
                console.log(
                  'Document type being uploaded:',
                  payload.idDocSetType,
                );
              }
            })
            .on('idCheck.onStepCompleted', (payload) => {
              console.log('Verification step completed:', payload);
              eventHandlers['idCheck.onStepCompleted'](payload);
              updateSdkInteractionStarted(true);
              if (payload.idDocSetType) {
                console.log('Document type completed:', payload.idDocSetType);
              }
            })
            .on('idCheck.onError', (error: unknown) => {
              captureAndReportError('Verification error:', error);
              eventHandlers['idCheck.onError'](error);
              setError(
                error instanceof Error
                  ? error.message
                  : 'Verification failed. Please try again.',
              );
            })
            .on('idCheck.onApplicantLoaded', (payload) => {
              console.log('Applicant loaded:', payload);
              eventHandlers['idCheck.onApplicantLoaded'](payload);
              if (payload.applicantId) {
                console.log('Applicant ID:', payload.applicantId);
              }
            })
            .on('idCheck.onApplicantSubmitted', (payload) => {
              console.log('Documents submitted:', payload);
              updateSdkInteractionStarted(true);
              eventHandlers['idCheck.onApplicantSubmitted'](payload);
            })
            .on('idCheck.onApplicantStatusChanged', async (payload) => {
              console.log('Applicant status changed:', payload);
              eventHandlers['idCheck.onApplicantStatusChanged'](payload);

              if (isResumingRef.current && !sdkInteractionStartedRef.current) {
                console.log('Ignoring initial status event for resuming user', {
                  reviewStatus: payload.reviewStatus,
                  isResuming: isResumingRef.current,
                  sdkInteractionStarted: sdkInteractionStartedRef.current,
                });
                return;
              }

              switch (payload.reviewStatus) {
                case 'init':
                  console.log('Verification initialized');
                  break;
                case 'pending':
                case 'queued':
                case 'prechecked':
                case 'awaitingService':
                  console.log('Verification submitted and under review');
                  {
                    const result = await markKybSubmissionAsSubmittedIfNeeded();
                    if (!result) {
                      return;
                    }
                  }
                  break;
                case 'onHold':
                case 'awaitingUser':
                  console.log(
                    'Verification on hold - additional information may be required',
                  );
                  {
                    const result = await markKybSubmissionAsSubmittedIfNeeded();
                    if (!result) {
                      return;
                    }
                  }
                  break;
                case 'completed': {
                  await handleFinalReviewResult(
                    payload as SumsubReviewPayload,
                    'idCheck.onApplicantStatusChanged',
                  );
                  break;
                }
                default:
                  console.log('Unknown status:', payload.reviewStatus);
              }
            })
            .on('idCheck.onApplicantVerificationCompleted', async (payload) => {
              console.log('Applicant verification completed:', payload);
              await handleFinalReviewResult(
                payload as SumsubReviewPayload,
                'idCheck.onApplicantVerificationCompleted',
              );
            })
            .on('idCheck.moduleResultPresented', async (payload) => {
              console.log('Module result presented:', payload);
              eventHandlers['idCheck.moduleResultPresented'](payload);
              switch (payload.answer) {
                case 'GREEN':
                  console.log(
                    'Module passed - awaiting final applicant review result',
                  );
                  break;
                case 'YELLOW':
                  console.log(
                    'Module returned YELLOW - final applicant result will be determined later',
                  );
                  setError('');
                  setNotice(
                    'Additional checks are still in progress. We will update your verification status after Sumsub finishes reviewing.',
                  );
                  break;
                case 'RED':
                  console.log('Module reported a failure');
                  setNotice('');
                  setError(
                    'Verification was not successful. Please contact support.',
                  );
                  return;
                default:
                  console.log('Unknown module answer:', payload.answer);
              }
            })
            .on('idCheck.onApplicantActionCompleted', async (payload) => {
              console.log('Applicant action completed:', payload);
              eventHandlers['idCheck.onApplicantActionCompleted'](payload);
              if (payload.answer === 'GREEN') {
                console.log(
                  'Applicant action completed successfully - awaiting final review result',
                );
              } else if (payload.answer === 'YELLOW') {
                console.log(
                  'Applicant action completed with YELLOW - awaiting final review result',
                );
                setError('');
                setNotice(
                  'The additional verification action is complete. Sumsub is still determining the final result.',
                );
              } else if (payload.answer === 'RED') {
                console.log('Action failed with RED status');
                setNotice('');
                setError(
                  'Additional verification action failed. Please contact support.',
                );
              } else {
                console.log('Unknown applicant action answer:', payload.answer);
              }
            })
            .on('idCheck.onResize', (payload) => {
              console.log('SDK resized:', payload);
              eventHandlers['idCheck.onResize'](payload);
            })
            .build();

          snsWebSdkInstance.launch('#sumsub-websdk-container');
        }
      }, 100);
    } catch (err) {
      captureAndReportError('SDK initialization error:', err);
      setError('Failed to initialize verification');
      setLoading(false);
    }
  };

  const restartVerification = async () => {
    setLoading(true);
    setError('');
    setNotice('');

    const resetResult = await resetVerification();
    if (!resetResult) {
      setLoading(false);
      return;
    }

    await initializeSumsubSdk({ forceFresh: true });
  };

  useEffect(() => {
    return () => {
      if (eventTrackerRef.current) {
        eventTrackerRef.current.flush();
      }
    };
  }, []);

  return {
    error,
    notice,
    loading,
    showSdk,
    initializeSumsubSdk,
    restartVerification,
  };
}
